/**
 * Capaciteitsbeheer-sectie (Epic D-1 — Fundament, Story D-1.3, UX §5.1)
 *
 * Het derde segment van het "Lijsten beheren"-menu: het dagcijfer per
 * medewerker en — als tweede blok binnen hetzelfde segment — de
 * organisatiebrede wegingsfactor per activiteit. Inline bewerken per veld,
 * hetzelfde patroon als `ListItemsSection` in `LijstenBeherenMenu.tsx`.
 *
 * PRD §9 is hier UI-eis, geen toelichting:
 * - de zin boven de tabel staat er letterlijk en volledig;
 * - geen realisatiecijfers, geen ranglijst, geen totaalregel, geen
 *   sortering op dagcijfer — de rijen staan vast alfabetisch op naam.
 */
import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { ApiRequestError } from '../../../lib/api';
import {
  useCapacityList,
  useUpdateDailyScore,
  useActivityWeights,
  useUpdateActivityWeight,
} from '../hooks/usePlanningCapacity';
import {
  PLANNING_TASK_ACTIVITIES,
  PLANNING_TASK_ACTIVITY_LABELS,
} from '@functieloonhuis/shared';
import type { ActivityWeightEntryDTO, PlanningTaskActivity } from '@functieloonhuis/shared';

/** PRD §9 / UX §5.1 — letterlijke copy, karakter voor karakter. Niet herformuleren. */
const CAPACITEIT_GRENS_ZIN =
  'Het dagcijfer verdeelt het werk over de dag. Het is geen norm en wordt nergens afgezet tegen wat iemand werkelijk heeft gedaan.';

const ONGELDIG_DAGCIJFER_MESSAGE = 'Voer een geheel getal groter dan nul in.';
const ONGELDIGE_FACTOR_MESSAGE = 'Voer een getal groter dan nul in.';
const OPSLAAN_MISLUKT_MESSAGE = 'Opslaan is niet gelukt';

/**
 * Weergave met Nederlandse decimaalkomma (1.5 → "1,5"), zónder
 * duizendtallengroepering en zonder afronding. `toLocaleString('nl-NL')`
 * groepeert (1000 → "1.000") en rondt op 3 decimalen af; via de
 * bewerk-roundtrip zou 1000 dan stilletjes 1 worden.
 */
function formatWeight(weight: number): string {
  return String(weight).replace('.', ',');
}

/**
 * Strikt: alleen cijfers met optioneel één komma- of punt-decimaaldeel.
 * `Number()` accepteert anders ook '1e3' en '0x10'.
 */
const WEIGHT_INPUT_PATTERN = /^\d+([.,]\d+)?$/;

/** Alleen een kaal geheel getal — geen '1e3', '0x10', teken of decimaal. */
const SCORE_INPUT_PATTERN = /^\d+$/;

function parseWeightInput(raw: string): number {
  return Number(raw.replace(',', '.'));
}

const inputClass =
  'w-24 rounded-md border border-[#E2E8F0] px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]';
const saveButtonClass = 'text-sm font-medium text-[#54949E] hover:underline';
const cancelButtonClass = 'text-sm text-gray-600 hover:underline';
const editButtonClass = 'text-sm font-medium text-[#54949E] hover:underline';

export function CapaciteitBeheerSectie() {
  const {
    data: capacity,
    isLoading: isCapacityLoading,
    isError: isCapacityError,
  } = useCapacityList();
  const {
    data: weights,
    isLoading: isWeightsLoading,
    isError: isWeightsError,
  } = useActivityWeights();
  const updateDailyScore = useUpdateDailyScore();
  const updateActivityWeight = useUpdateActivityWeight();
  const { showToast } = useToast();

  const [editingEmployeeId, setEditingEmployeeId] = useState<number | null>(null);
  const [editingScoreValue, setEditingScoreValue] = useState('');
  const [scoreError, setScoreError] = useState<string | null>(null);

  const [editingActivity, setEditingActivity] = useState<PlanningTaskActivity | null>(null);
  const [editingWeightValue, setEditingWeightValue] = useState('');
  const [weightError, setWeightError] = useState<string | null>(null);

  // PRD §9: vast alfabetisch op naam — nooit op dagcijfer.
  const employees = [...(capacity ?? [])].sort((a, b) =>
    a.employeeName.localeCompare(b.employeeName, 'nl')
  );
  // Een mislukte GET is een fouttoestand, geen lege lijst — anders vermomt een
  // serverfout zich als "Geen medewerkers gevonden".
  const showEmptyState = !isCapacityLoading && !isCapacityError && employees.length === 0;

  const weightByActivity = new Map<PlanningTaskActivity, ActivityWeightEntryDTO>(
    (weights ?? []).map((entry) => [entry.activity, entry])
  );

  // De focus volgt de bewerkmodus (code review 2026-08-19). Zonder dit valt de
  // focus na "Bewerken", "Opslaan" of "Annuleren" terug op <body>, omdat de knop
  // die net is ingedrukt uit de boom verdwijnt. In een tabel met tientallen rijen
  // betekent dat opnieuw tabben vanaf de bovenkant van het venster.
  const scoreInputRef = useRef<HTMLInputElement | null>(null);
  const weightInputRef = useRef<HTMLInputElement | null>(null);
  const scoreReturnFocusTo = useRef<number | null>(null);
  const weightReturnFocusTo = useRef<PlanningTaskActivity | null>(null);

  useEffect(() => {
    if (editingEmployeeId !== null) {
      scoreInputRef.current?.focus();
      scoreInputRef.current?.select();
      return;
    }
    const terug = scoreReturnFocusTo.current;
    if (terug === null) return;
    scoreReturnFocusTo.current = null;
    document
      .querySelector<HTMLButtonElement>(`[data-testid="capaciteit-bewerken-${terug}"]`)
      ?.focus();
  }, [editingEmployeeId]);

  useEffect(() => {
    if (editingActivity !== null) {
      weightInputRef.current?.focus();
      weightInputRef.current?.select();
      return;
    }
    const terug = weightReturnFocusTo.current;
    if (terug === null) return;
    weightReturnFocusTo.current = null;
    document.querySelector<HTMLButtonElement>(`[data-testid="weging-bewerken-${terug}"]`)?.focus();
  }, [editingActivity]);

  function startScoreEdit(employeeId: number, dailyScore: number) {
    scoreReturnFocusTo.current = employeeId;
    setEditingEmployeeId(employeeId);
    setEditingScoreValue(String(dailyScore));
    setScoreError(null);
  }

  async function handleSaveScore(employeeId: number) {
    const trimmed = editingScoreValue.trim();
    const parsed = Number(trimmed);
    if (!SCORE_INPUT_PATTERN.test(trimmed) || parsed <= 0) {
      setScoreError(ONGELDIG_DAGCIJFER_MESSAGE);
      return;
    }
    setScoreError(null);
    try {
      await updateDailyScore.mutateAsync({ employeeId, dailyScore: parsed });
      setEditingEmployeeId(null);
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'VALIDATION_ERROR') {
        // De servermelding is specifieker dan de client-tekst (bijv. een
        // bovengrens op een verder geldig geheel getal) — gooi hem niet weg.
        setScoreError(error.message || ONGELDIG_DAGCIJFER_MESSAGE);
      } else {
        // Bewust OPEN laten (code review 2026-08-19). Eerder sloot dit het veld,
        // met als motivering dat het oorspronkelijke dagcijfer dan weer in beeld
        // staat. Dat klopt, maar het gooit ook weg wat iemand net had ingetypt:
        // na een haperende verbinding moet het getal opnieuw. De invoer blijft
        // nu staan, zodat Opslaan een tweede keer indrukken genoeg is.
        showToast(OPSLAAN_MISLUKT_MESSAGE, 'error');
      }
    }
  }

  function startWeightEdit(activity: PlanningTaskActivity, weight: number) {
    weightReturnFocusTo.current = activity;
    setEditingActivity(activity);
    setEditingWeightValue(formatWeight(weight));
    setWeightError(null);
  }

  async function handleSaveWeight(activity: PlanningTaskActivity) {
    const trimmed = editingWeightValue.trim();
    const parsed = parseWeightInput(trimmed);
    if (!WEIGHT_INPUT_PATTERN.test(trimmed) || parsed <= 0) {
      setWeightError(ONGELDIGE_FACTOR_MESSAGE);
      return;
    }
    setWeightError(null);
    try {
      await updateActivityWeight.mutateAsync({ activity, weight: parsed });
      setEditingActivity(null);
    } catch (error) {
      if (error instanceof ApiRequestError && error.code === 'VALIDATION_ERROR') {
        setWeightError(error.message || ONGELDIGE_FACTOR_MESSAGE);
      } else {
        // Open laten, net als bij het dagcijfer: een haperende verbinding hoort
        // de ingetypte waarde niet weg te gooien (code review 2026-08-19).
        showToast(OPSLAAN_MISLUKT_MESSAGE, 'error');
      }
    }
  }

  return (
    <div>
      <p data-testid="capaciteit-grens-zin" className="mb-4 text-sm text-gray-700">
        {CAPACITEIT_GRENS_ZIN}
      </p>

      {isCapacityLoading && (
        <p data-testid="capaciteit-laden" className="py-6 text-center text-sm text-gray-500">
          Laden…
        </p>
      )}

      {isCapacityError && (
        <p data-testid="capaciteit-fout" className="py-6 text-center text-sm text-[#D64545]">
          Ophalen is niet gelukt. Probeer het opnieuw.
        </p>
      )}

      {showEmptyState && (
        <p data-testid="capaciteit-lege-staat" className="py-6 text-center text-sm text-gray-500">
          Geen medewerkers gevonden.
        </p>
      )}

      {!isCapacityLoading && !isCapacityError && employees.length > 0 && (
        <table data-testid="capaciteit-tabel" className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-left text-gray-600">
              <th className="py-2 font-medium">Medewerker</th>
              {/* PRD §9: geen sorteerknop, geen aria-sort — dit is geen ranglijst. */}
              <th data-testid="capaciteit-kolom-dagcijfer" className="py-2 font-medium">
                Dagcijfer
              </th>
              <th className="py-2">
                <span className="sr-only">Acties</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {employees.map((entry) => (
              <tr key={entry.employeeId} data-testid={`capaciteit-rij-${entry.employeeId}`}>
                <td className="py-2 text-[#1E293B]">{entry.employeeName}</td>
                {editingEmployeeId === entry.employeeId ? (
                  <td colSpan={2} className="py-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        data-testid={`capaciteit-dagcijfer-input-${entry.employeeId}`}
                        ref={scoreInputRef}
                        aria-label={`Dagcijfer van ${entry.employeeName}`}
                        value={editingScoreValue}
                        onChange={(e) => {
                          setEditingScoreValue(e.target.value);
                          setScoreError(null);
                        }}
                        aria-invalid={!!scoreError}
                        aria-describedby={
                          scoreError ? `capaciteit-dagcijfer-fout-${entry.employeeId}` : undefined
                        }
                        className={inputClass}
                      />
                      <button
                        type="button"
                        data-testid={`capaciteit-dagcijfer-opslaan-${entry.employeeId}`}
                        onClick={() => handleSaveScore(entry.employeeId)}
                        disabled={updateDailyScore.isPending}
                        className={saveButtonClass}
                      >
                        Opslaan
                      </button>
                      <button
                        type="button"
                        data-testid={`capaciteit-dagcijfer-annuleren-${entry.employeeId}`}
                        onClick={() => setEditingEmployeeId(null)}
                        className={cancelButtonClass}
                      >
                        Annuleren
                      </button>
                    </div>
                    <p
                      data-testid={`capaciteit-dagcijfer-${entry.employeeId}`}
                      className="mt-1 text-xs text-gray-600"
                    >
                      Opgeslagen: {entry.dailyScore}
                    </p>
                    {scoreError && (
                      <p
                        id={`capaciteit-dagcijfer-fout-${entry.employeeId}`}
                        data-testid={`capaciteit-dagcijfer-fout-${entry.employeeId}`}
                        role="alert"
                        className="mt-1 text-xs text-[#D64545]"
                      >
                        {scoreError}
                      </p>
                    )}
                  </td>
                ) : (
                  <>
                    <td
                      data-testid={`capaciteit-dagcijfer-${entry.employeeId}`}
                      className="py-2 text-[#1E293B]"
                    >
                      {entry.dailyScore}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        data-testid={`capaciteit-bewerken-${entry.employeeId}`}
                        onClick={() => startScoreEdit(entry.employeeId, entry.dailyScore)}
                        className={editButtonClass}
                      >
                        Bewerken
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Tweede blok binnen hetzelfde segment (AC 2): de organisatiebrede wegingsfactoren. */}
      <h3 className="mb-2 mt-6 text-sm font-medium text-gray-900">Wegingsfactor per activiteit</h3>

      {isWeightsLoading && (
        <p data-testid="weging-laden" className="py-4 text-center text-sm text-gray-500">
          Laden…
        </p>
      )}

      {isWeightsError && (
        <p data-testid="weging-fout-laden" className="py-4 text-center text-sm text-[#D64545]">
          Ophalen is niet gelukt. Probeer het opnieuw.
        </p>
      )}

      {!isWeightsLoading && !isWeightsError && (
        <table data-testid="weging-tabel" className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E2E8F0] text-left text-gray-600">
              <th className="py-2 font-medium">Activiteit</th>
              <th className="py-2 font-medium">Wegingsfactor</th>
              <th className="py-2">
                <span className="sr-only">Acties</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {PLANNING_TASK_ACTIVITIES.map((activity) => {
              const entry = weightByActivity.get(activity);
              // Geen stille terugval op 1 (code review 2026-08-19). De backend
              // hoort alle zes activiteiten terug te geven — een ontbrekende
              // rij is daar de standaardwaarde 1,0, maar dat is de backend die
              // dat zegt, niet dit scherm dat het raadt. Zou hier `?? 1` staan,
              // dan toont een backend die er nog maar vijf teruggeeft doodleuk
              // 1,0 als de echte factor, en schrijft Opslaan die 1,0 ook weg.
              const weight = entry?.weight;
              return (
                <tr key={activity} data-testid={`weging-rij-${activity}`}>
                  <td className="py-2 text-[#1E293B]">
                    {PLANNING_TASK_ACTIVITY_LABELS[activity]}
                  </td>
                  {editingActivity === activity ? (
                    <td colSpan={2} className="py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          data-testid={`weging-input-${activity}`}
                          ref={weightInputRef}
                          aria-label={`Wegingsfactor voor ${PLANNING_TASK_ACTIVITY_LABELS[activity]}`}
                          value={editingWeightValue}
                          onChange={(e) => {
                            setEditingWeightValue(e.target.value);
                            setWeightError(null);
                          }}
                          aria-invalid={!!weightError}
                          aria-describedby={weightError ? `weging-fout-${activity}` : undefined}
                          className={inputClass}
                        />
                        <button
                          type="button"
                          data-testid={`weging-opslaan-${activity}`}
                          onClick={() => handleSaveWeight(activity)}
                          disabled={updateActivityWeight.isPending}
                          className={saveButtonClass}
                        >
                          Opslaan
                        </button>
                        <button
                          type="button"
                          data-testid={`weging-annuleren-${activity}`}
                          onClick={() => setEditingActivity(null)}
                          className={cancelButtonClass}
                        >
                          Annuleren
                        </button>
                      </div>
                      <p
                        data-testid={`weging-factor-${activity}`}
                        className="mt-1 text-xs text-gray-600"
                      >
                        Opgeslagen: {weight === undefined ? 'niet opgehaald' : formatWeight(weight)}
                      </p>
                      {weightError && (
                        <p
                          id={`weging-fout-${activity}`}
                          data-testid={`weging-fout-${activity}`}
                          role="alert"
                          className="mt-1 text-xs text-[#D64545]"
                        >
                          {weightError}
                        </p>
                      )}
                    </td>
                  ) : (
                    <>
                      <td
                        data-testid={`weging-factor-${activity}`}
                        className={weight === undefined ? 'py-2 text-[#D64545]' : 'py-2 text-[#1E293B]'}
                      >
                        {weight === undefined ? 'Niet opgehaald' : formatWeight(weight)}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          data-testid={`weging-bewerken-${activity}`}
                          // Niet te bewerken zolang de huidige waarde onbekend is:
                          // Opslaan zou dan een geraden getal wegschrijven over een
                          // factor die misschien allang is bijgesteld.
                          disabled={weight === undefined}
                          title={
                            weight === undefined
                              ? 'De huidige wegingsfactor is niet opgehaald — vernieuw eerst.'
                              : undefined
                          }
                          onClick={() => weight !== undefined && startWeightEdit(activity, weight)}
                          className={
                            weight === undefined
                              ? 'text-sm font-medium text-gray-400'
                              : editButtonClass
                          }
                        >
                          Bewerken
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
